<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import HeaderComponent from "@/components/ui/HeaderComponent.vue";
import HamburgerMenu from "@/components/ui/HamburgerMenu.vue";
import ModalComponent from "@/components/ui/ModalComponent.vue";

// Modal contents
import NewContactForm from "@/components/ui/modalContents/NewContactForm.vue";
import ContactInfo from "@/components/ui/modalContents/LeadInfo.vue";
import NewTag from "@/components/ui/modalContents/NewTag.vue";
import NewReminder from "@/components/ui/modalContents/NewReminder.vue";
import EditReminder from "@/components/ui/modalContents/EditReminder.vue";
import NewContact from "@/components/ui/modalContents/AddContact.vue";
import EditContact from "@/components/ui/modalContents/EditContact.vue";
import AddTagSearch from "@/components/ui/modalContents/AddTagSearch.vue";
import ContactModal from "@/components/ui/modalContents/ContactModal.vue";
import ChangePassword from "./components/ui/modalContents/ChangePassword.vue";

import eventBus from "@/services/eventBus";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Echo from "laravel-echo";
import Swal from "sweetalert2";
/* eslint-disable no-unused-vars */
import Pusher from "pusher-js";
/* eslint-enable no-unused-vars */

// Vuex store and route setup
const store = useStore();
const route = useRoute();

// Modal and route state management
const isModalOpen = computed(() => store.state.uiSlice.isModalOpen);
const isStandalonePage = computed(
  () => route.path === "/login" || route.path === "/version",
);
const modalType = ref("");
const modalTitle = ref("");
const isDarkMode = computed(() => store.state.uiSlice.isDark);

const MODAL_COMPONENTS = {
  newContact: NewContactForm,
  contactInfo: ContactInfo,
  newTag: NewTag,
  newReminder: NewReminder,
  editReminder: EditReminder,
  addContact: NewContact,
  editContact: EditContact,
  addTagSearch: AddTagSearch,
  contactModal: ContactModal,
  changePassword: ChangePassword,
};

const currentModalComponent = computed(
  () => MODAL_COMPONENTS[modalType.value] || null,
);

// WebSocket and user authentication state
const currentUser = computed(() => store.state.userSlice.user);
let echo; // Echo instance to manage WebSocket subscriptions

// Subscribe to WebSocket channel for the user
const subscribeToChannel = () => {
  if (!currentUser.value) {
    console.error("User data is not available. Cannot subscribe to channel.");
    return;
  }

  echo = new Echo({
    broadcaster: "pusher",
    key: process.env.VUE_APP_PUSHER_APP_KEY, // Use environment variable for Pusher key
    cluster: process.env.VUE_APP_PUSHER_APP_CLUSTER, // Use environment variable for Pusher cluster
    encrypted: true,
  });

  // Listen to user-specific channel events
  echo
    .channel(`channel_${currentUser.value.id}`)
    .listen("WebSocketContactNotifier", (event) => {
      Swal.fire({
        title: `شماره ای که تماس میگیرد با شما: ${event.phone_number}`,
        icon: "info",
        confirmButtonText: "بستن",
        showCloseButton: true,
        html: `
          <div id="contactDetails" style="text-align: center; font-size: 18px;"></div>
          <div class="swal-footer" style="display: flex; justify-content: center; gap: 20px;">
            ${
              event.last_lead_id &&
              `<button id="goToLead" class="swal2-confirm swal2-styled">برو به آخرین لید</button>
            `
            }
            ${
              event.contact_id &&
              `<button id="goToContactPage" class="swal2-confirm swal2-styled">برو به صفحه مخاطب</button>`
            }
          </div>
        `,
        didOpen: () => {
          const contactDetails = document.getElementById("contactDetails");
          if (event.contact_id !== null) {
            contactDetails.innerHTML = `<p>نام کانتکت شما : ${event.contact_name}</p>`;
          } else {
            contactDetails.innerHTML = `<p>کانتکت پیدا نشد</p>`;
          }

          // Button click handlers
          const leadBtn = document.getElementById("goToLead");
          if (leadBtn) {
            leadBtn.addEventListener("click", () => {
              window.open(`leads/${event.last_lead_id}`, "_blank");
            });
          }

          const contactBtn = document.getElementById("goToContactPage");
          if (contactBtn) {
            contactBtn.addEventListener("click", () => {
              window.open(`contacts/${event.contact_id}`, "_blank");
            });
          }
        },
      });
    });
};

// Clean up Echo instance to prevent memory leaks
const cleanupEcho = () => {
  if (echo) {
    echo.disconnect();
    echo = null;
  }
};

// Modal open/close event listeners
const openModal = ({ contentType, title }) => {
  modalType.value = contentType;
  modalTitle.value = title;
  store.dispatch("uiSlice/openModal");
};

const closeModal = () => {
  store.commit("remindersSlice/resetDate");
  store.dispatch("uiSlice/closeModal");
};

// Watcher for currentUser
watch(
  currentUser,
  (newVal) => {
    if (newVal) {
      subscribeToChannel(); // Subscribe when currentUser becomes available
    }
  },
  { immediate: true }, // Trigger the watcher immediately on component mount
);

// Lifecycle hooks
onMounted(() => {
  eventBus.on("open-modal", openModal);
  eventBus.on("close-modal", closeModal);
});

onUnmounted(() => {
  cleanupEcho(); // Clean up WebSocket instance
  eventBus.off("open-modal", openModal);
  eventBus.off("close-modal", closeModal);
});
</script>

<template>
  <div
    v-if="!isStandalonePage"
    :class="[
      'font-vazirmatn',
      'h-screen',
      'flex',
      'flex-col',
      { 'dark bg-neutral-800': isDarkMode },
    ]"
  >
    <HeaderComponent />
    <div class="flex flex-1">
      <HamburgerMenu class="shrink-0" />
      <router-view class="flex-1 mt-24" />
    </div>

    <ModalComponent
      :title="modalTitle"
      :is-visible="isModalOpen"
      @close="closeModal"
    >
      <component
        :is="currentModalComponent"
        v-if="currentModalComponent"
        :key="modalType"
        @close="closeModal"
      />
    </ModalComponent>
  </div>

  <div v-else class="font-vazirmatn">
    <router-view />
  </div>
</template>

<style>
#contactDetails,
#goToLead,
#goToContactPage,
.swal-footer {
  @apply font-vazirmatn;
}

/* Vue Select Dark Mode Styles */
.dark .v3s__combobox {
  @apply bg-neutral-700 border-neutral-600 text-neutral-200;
}

.dark .v3s__section {
  @apply bg-neutral-700 border-neutral-600;
}

.dark .v3s__option {
  @apply text-neutral-200 hover:bg-neutral-600;
}

.dark .v3s__option--selected {
  @apply bg-blue-primary text-white;
}

.dark .v3s__option--highlight {
  @apply bg-neutral-600;
}

.dark .v3s__combobox::placeholder {
  @apply text-neutral-400;
}

.dark .v3s__combobox:focus {
  @apply border-blue-primary outline-none;
}
</style>
